import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_PROTESE } from "@/lib/ajuda/dialogos/protese";
import { usePodeEditarParceiro } from "@/lib/use-pode-editar-parceiro";
import type { ProsthesisLabRow } from "./types";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls =
  "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";

type Props = {
  open: boolean;
  lab: ProsthesisLabRow | null;
  onClose: () => void;
};

/** Só edita — cadastro de laboratório novo agora é via busca/convite (ProsthesisLabAddDialog). */
export function ProsthesisLabDialog({ open, lab, onClose }: Props) {
  const utils = trpc.useUtils();
  const podeEditar = usePodeEditarParceiro();
  // Laboratório conectado a uma organização real: nome, telefone e endereço são espelho do perfil
  // dele — a API recusa alteração nesses campos, então a tela nem oferece.
  const vindoDoPerfil = (lab as any)?.origemDoDado === "perfil";
  const travado = vindoDoPerfil || !podeEditar;
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    state: "",
    specialty: "",
    notes: "",
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: lab?.name ?? "",
        phone: lab?.phone ?? "",
        email: lab?.email ?? "",
        city: lab?.city ?? "",
        state: lab?.state ?? "",
        specialty: lab?.specialty ?? "",
        notes: lab?.notes ?? "",
      });
    }
  }, [open, lab]);

  const update = trpc.prosthesis.lab.update.useMutation({
    onSuccess: () => {
      toast.success("Laboratório atualizado.");
      utils.prosthesis.lab.list.invalidate();
      utils.prosthesis.lab.options.invalidate();
      onClose();
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar."),
  });

  if (!open || !lab) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!lab) return;
    if (form.name.trim().length < 2) {
      toast.error("Informe o nome do laboratório.");
      return;
    }
    update.mutate(
      vindoDoPerfil
        ? {
            id: lab.id,
            email: form.email || undefined,
            specialty: form.specialty || undefined,
            notes: form.notes || undefined,
          }
        : {
            id: lab.id,
            name: form.name.trim(),
            phone: form.phone || undefined,
            email: form.email || undefined,
            city: form.city || undefined,
            state: (form.state || undefined) as never,
            specialty: form.specialty || undefined,
            notes: form.notes || undefined,
          },
    );
  }

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // Montava o próprio overlay, sem `max-h` nem rolagem: em 390x664 o formulário media 719px, o topo
  // ficava em -27px e o botão Salvar caía fora da janela, sem barra de rolagem nenhuma — no celular
  // era impossível salvar. Usando o DialogContent compartilhado ele herda `max-h-[90vh]`, rolagem,
  // ESC, clique fora, foco preso e o X com área de toque de verdade.
  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle ajuda={AJUDA_PROTESE.editarLaboratorio}>Editar laboratório</DialogTitle>
          </DialogHeader>

          {vindoDoPerfil && (
            <p className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12px] leading-relaxed text-zinc-600 dark:border-border dark:bg-muted/30 dark:text-muted-foreground">
              <span className="font-semibold text-zinc-800 dark:text-foreground">Laboratório conectado.</span>{" "}
              Nome, telefone e cidade vêm do perfil dele e são atualizados por ele. Continuam seus:
              e-mail de contato, especialidade e observações.
            </p>
          )}
          {!podeEditar && (
            <p className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12px] leading-relaxed text-zinc-600 dark:border-border dark:bg-muted/30 dark:text-muted-foreground">
              Cadastro de parceiro é editado por administradores da organização.
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-12">
            <div className="sm:col-span-12">
              <label className={labelCls}>Nome</label>
              <input value={form.name} onChange={set("name")} disabled={travado} className={inputCls} autoFocus />
            </div>
            <div className="sm:col-span-6">
              <label className={labelCls}>Telefone</label>
              <input value={form.phone} onChange={set("phone")} disabled={travado} className={inputCls} />
            </div>
            <div className="sm:col-span-6">
              <label className={labelCls}>E-mail</label>
              <input value={form.email} onChange={set("email")} disabled={!podeEditar} className={inputCls} />
            </div>
            <div className="sm:col-span-7">
              <label className={labelCls}>Cidade</label>
              <input value={form.city} onChange={set("city")} disabled={travado} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>UF</label>
              <input
                value={form.state}
                onChange={set("state")}
                maxLength={2}
                disabled={travado}
                className={`${inputCls} uppercase`}
              />
            </div>
            <div className="sm:col-span-3">
              <label className={labelCls}>Especialidade</label>
              <input value={form.specialty} onChange={set("specialty")} disabled={!podeEditar} className={inputCls} />
            </div>
            <div className="sm:col-span-12">
              <label className={labelCls}>Observações</label>
              <textarea
                value={form.notes}
                onChange={set("notes")}
                rows={2}
                className="w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
              />
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="h-10 flex-1 rounded-md border border-zinc-200 px-4 text-sm font-medium text-zinc-700 hover:bg-zinc-50 sm:h-9 sm:flex-none dark:border-border dark:text-foreground dark:hover:bg-muted"
            >
              {podeEditar ? "Cancelar" : "Fechar"}
            </button>
            {podeEditar && (
              <button
                type="submit"
                disabled={update.isPending}
                className="inline-flex h-10 flex-[1.4] items-center justify-center gap-2 rounded-md bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-60 sm:h-9 sm:flex-none"
              >
                {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Salvar
              </button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
